import { Alert, Button, Checkbox, Descriptions, Modal, Popconfirm, Space, Table, Tag, message } from 'antd';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import container from '@/lib/di/container';
import { ElectronicContractRepo } from '@/core/repos/tables/electronic-contract.repo';
import { ElectronicContractLifecycle, ElectronicContractModel } from '@/core/models/electronic-contract.model';
import { canCompanySign, documentTypeLabels } from '@/core/utils/electronic-contract';
import { formatDateTimePipe } from '@/core/pipes/format-date.pipe';
import { readSignatureImage } from '../signature-image';
import { ViewDocumentModal } from './ViewDocumentModal';
import { ApiResponse } from '@/core/models/response.model';

const repo = container.get(ElectronicContractRepo);

function acceptLifecycle(response: ApiResponse<ElectronicContractLifecycle>, current: ElectronicContractLifecycle | null,
    record: ElectronicContractModel): ElectronicContractLifecycle {
    if (response.code === '00') return response.result;
    // An unsuccessful save/sync may return newer provider data that is not durable.
    // Keep confirmed progress while preserving the barrier for the attempted action.
    return {
        id: record.id, documentId: record.documentId || '',
        status: current?.status || record.status || 'Chưa xác nhận',
        providerStatus: current?.providerStatus || record.providerStatus || 'Chưa xác nhận',
        lastSyncedAt: current?.lastSyncedAt || record.lastSyncedAt,
        signedAt: current?.signedAt || record.signedAt,
        signers: current?.signers || [],
        companySignState: response.result.companySignState || current?.companySignState,
        companySignAttemptedAt: response.result.companySignAttemptedAt || current?.companySignAttemptedAt,
        requiresReconciliation: true,
    };
}

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: ElectronicContractModel;
    canSign: boolean;
    scope: string;
    onSuccess?: () => void;
}

export const ContractLifecycleModal = ({ isModalOpen, setIsModalOpen, data, canSign, scope, onSuccess }: Props) => {
    const [lifecycle, setLifecycle] = useState<ElectronicContractLifecycle | null>(null);
    const [loading, setLoading] = useState(false);
    const [signing, setSigning] = useState(false);
    const [error, setError] = useState('');
    const [image, setImage] = useState('');
    const [imageName, setImageName] = useState('');
    const [viewOpen, setViewOpen] = useState(false);
    const [viewed, setViewed] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [pending, setPending] = useState(false);
    const generation = useRef(0);
    const inFlight = useRef(false);
    const [messageApi, contextHolder] = message.useMessage();
    const markerKey = `econtract-company-sign:${scope}:${data.id}`;

    const synchronize = useCallback(async () => {
        const version = generation.current;
        setLoading(true);
        setError('');
        try {
            const response = await repo.syncContract(data.id);
            if (version !== generation.current) return;
            if (response?.result) {
                setLifecycle(current => acceptLifecycle(response, current, data));
                if (response.code === '00' && ['Rejected', 'VerifiedSigned'].includes(response.result.companySignState || '')) {
                    localStorage.removeItem(markerKey);
                    setPending(false);
                }
            } else {
                setLifecycle(current => current ? { ...current, requiresReconciliation: true } : null);
            }
            if (response?.code !== '00') setError(response?.errorMessage || 'Chưa đồng bộ được trạng thái. Vui lòng thử đồng bộ lại.');
            onSuccess?.();
        } catch {
            if (version === generation.current) {
                setLifecycle(current => current ? { ...current, requiresReconciliation: true } : null);
                setError('Mất kết nối khi đồng bộ. Chưa thể xác nhận lượt ký.');
            }
        } finally { if (version === generation.current) setLoading(false); }
    }, [data, markerKey, onSuccess]);

    useEffect(() => {
        generation.current += 1;
        setLifecycle(null);
        setImage(''); setImageName(''); setViewed(false); setConfirmed(false); setViewOpen(false); setError('');
        if (isModalOpen && data.id) {
            // The marker contains no image, document or identity information.
            try { setPending(localStorage.getItem(markerKey) === 'pending'); }
            catch { setPending(true); setError('Không đọc được trạng thái lần ký trước trên trình duyệt.'); }
            synchronize();
        }
        return () => { generation.current += 1; };
    }, [isModalOpen, data.id, markerKey, synchronize]);

    const sign = async () => {
        if (inFlight.current || !canSign || pending || !viewed || !confirmed || !image || !canCompanySign(lifecycle, data.documentType)) return;
        // Persist the local barrier before sending. Failed persistence must not send a signature.
        try { localStorage.setItem(markerKey, 'pending'); }
        catch { setError('Không lưu được trạng thái yêu cầu ký trên trình duyệt. Vui lòng kiểm tra bộ nhớ trình duyệt.'); return; }
        inFlight.current = true; setSigning(true); setPending(true); setError('');
        try {
            const response = await repo.companySign({ electronicContractId: data.id, fileContentSignatureImage: image });
            setImage(''); setImageName(''); setConfirmed(false);
            if (response?.result) {
                setLifecycle(current => acceptLifecycle(response, current, data));
                if (response.code === '00' && response.result.companySignState === 'VerifiedSigned') {
                    localStorage.removeItem(markerKey); setPending(false);
                }
            }
            if (response?.code === '00') messageApi.success('Đã tiếp nhận yêu cầu ký. Kiểm tra trạng thái từng bên để xác nhận hoàn tất.');
            else setError(response?.errorMessage || 'Chưa xác định kết quả ký. Đồng bộ và đối soát trước khi thao tác tiếp.');
            onSuccess?.();
        } catch { setError('Mất kết nối khi ký. Yêu cầu đã được khóa để đối soát; không gửi ký lại.'); }
        finally { inFlight.current = false; setSigning(false); }
    };

    const eligible = canSign && !loading && !signing && !pending && canCompanySign(lifecycle, data.documentType);
    const close = () => { if (!signing) setIsModalOpen(false); };

    return <Modal title="Chi tiết hợp đồng điện tử" open={isModalOpen} onCancel={close} width={940}
        maskClosable={!signing} closable={!signing} keyboard={!signing} footer={<Button onClick={close} disabled={signing}>Đóng</Button>}>
        {contextHolder}
        <Space direction="vertical" size="middle" className="w-full">
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }} items={[
                { key: 'customer', label: 'Khách hàng', children: `${data.waterUserCode || ''} — ${data.customerName || ''}` },
                { key: 'document', label: 'Loại hồ sơ', children: documentTypeLabels[data.documentType || ''] || data.documentType || 'Hồ sơ cũ' },
                { key: 'code', label: 'Mã chứng từ', children: data.documentCode || '—' },
                { key: 'key', label: 'Khóa đối soát', children: data.requestKey || data.id },
                { key: 'status', label: 'Trạng thái WaterMS', children: lifecycle?.status || data.status || '—' },
                { key: 'provider', label: 'Trạng thái Mắt Bão', children: lifecycle?.providerStatus || data.providerStatus || '—' },
                { key: 'synced', label: 'Đồng bộ lúc', children: formatDateTimePipe(lifecycle?.lastSyncedAt || data.lastSyncedAt || '') || '—' },
                { key: 'company', label: 'Lần ký công ty', children: lifecycle?.companySignState || 'Chưa ghi nhận' },
            ]} />
            <Space wrap>
                <Button onClick={synchronize} loading={loading} disabled={signing}>Đồng bộ trạng thái</Button>
                <Button onClick={() => setViewOpen(true)} disabled={!data.documentId || signing}>Xem PDF</Button>
            </Space>
            {error && <Alert type="error" showIcon message={error} />}
            {(pending || lifecycle?.requiresReconciliation || ['Creating', 'CreateUncertain'].includes(data.status || '')) &&
                <Alert type="warning" showIcon message="Hồ sơ cần đồng bộ hoặc đối soát"
                    description="Thao tác ký được khóa. Dùng mã hồ sơ và khóa đối soát để kiểm tra kết quả lần gửi trước." />}
            <Table size="small" pagination={false} rowKey="key"
                dataSource={(lifecycle?.signers || []).map((signer, index) => ({ ...signer, key: `${signer.signerIndex ?? 'unknown'}-${index}` }))}
                columns={[
                    { title: 'Vai ký', render: (_, signer) => signer.signerIndex ? `Bên ${signer.signerIndex} · ${signer.signerType || 'Chưa xác định'}` : 'Chưa xác định' },
                    { title: 'Người ký', dataIndex: 'fullName' },
                    { title: 'Trạng thái', dataIndex: 'status', render: status => <Tag color={status === 'DONE' ? 'success' : 'default'}>{status || 'Chưa xác định'}</Tag> },
                ]} />
            <Alert type="info" showIcon message="Khách hàng ký từ thư mời Mắt Bão. Công ty ký Server khi đã đến lượt."
                description="Mẫu khảo sát có thêm người khảo sát ký trước công ty. Hồ sơ chỉ hoàn tất khi Mắt Bão trả DONE và các bên đã ký đầy đủ." />
            {canSign && <div className="border-t border-gray-200 pt-4">
                <p className="font-semibold mb-2">Chữ ký số Server của công ty</p>
                <p className="text-gray-600 mb-3">Xem PDF, chọn ảnh chữ ký PNG/JPEG đã được công ty phê duyệt (tối đa 5 MB), rồi xác nhận ký.</p>
                <input aria-label="Ảnh chữ ký công ty" type="file" accept="image/png,image/jpeg" disabled={!eligible}
                    onChange={async event => {
                        const file = event.target.files?.[0];
                        setImage(''); setImageName(''); setConfirmed(false);
                        if (!file) return;
                        const version = generation.current;
                        try {
                            const content = await readSignatureImage(file);
                            if (version === generation.current) { setImage(content); setImageName(file.name); }
                        } catch (error) { if (version === generation.current) setError(error instanceof Error ? error.message : 'Không đọc được ảnh.'); }
                        event.target.value = '';
                    }} />
                {imageName && <p className="mt-2">Đã chọn: {imageName}</p>}
                <div className="my-3"><Checkbox checked={confirmed} disabled={!eligible || !viewed || !image}
                    onChange={event => setConfirmed(event.target.checked)}>Tôi đã xem PDF và xác nhận công ty ký đúng chứng từ này.</Checkbox></div>
                <Popconfirm title="Ký số hợp đồng bằng chứng thư công ty?" description="Yêu cầu được gửi một lần tới Mắt Bão."
                    okText="Xác nhận ký" cancelText="Hủy" onConfirm={sign} disabled={!eligible || !viewed || !image || !confirmed}>
                    <Button type="primary" loading={signing} disabled={!eligible || !viewed || !image || !confirmed}>Công ty ký Server</Button>
                </Popconfirm>
                {!eligible && <p className="text-gray-500 mt-2">Chưa xác nhận đến lượt công ty hoặc hồ sơ đang cần đối soát.</p>}
            </div>}
        </Space>
        <ViewDocumentModal isModalOpen={viewOpen} setIsModalOpen={setViewOpen} contractId={data.id} isV2 onLoaded={() => setViewed(true)} />
    </Modal>;
};
